// src/pages/ManagedAuthorTitlesPage.jsx
// Lists all titles for a publisher's managed author
// Mirrors /author/titles functionality with publisher scoping
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import PublisherNav from "../components/PublisherNav";
import UniversalFooter from "../components/UniversalFooter";
import StreamlinedUploadFlow from "../components/StreamlinedUploadFlow";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import BSPDetailModal from '../components/BSPDetailModal';
import { 
  ArrowLeft, 
  Plus, 
  Upload, 
  BookOpen, 
  Play, 
  BarChart3, 
  Users, 
  Clock,
  Eye,
  Settings,
  Trash2,
  Power,
  PowerOff,
  MoreVertical,
  User,
  RefreshCw,
  Music,
  CheckCircle,
  AlertCircle,
  Loader2,
  Brain
} from "lucide-react";
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/config/api';
import { xhrFetchAuth, xhrFetchAuthPost } from '@/lib/xhrFetch';
import ActivationChargeConfirmModal from '../components/ActivationChargeConfirmModal';
import PaymentMethodRequiredModal from '../components/PaymentMethodRequiredModal';
import { getBookCoverUrl, hasCoverImage } from '../lib/coverUtils';

const API = `${BACKEND_URL}/api`;

const statusColors = {
  active: "bg-emerald-500/15 text-emerald-300 ring-emerald-400/40",
  ready: "bg-blue-500/15 text-blue-300 ring-blue-400/40",
  audio_pending: "bg-amber-500/10 text-amber-200 ring-amber-400/40",
  outline_pending: "bg-orange-500/10 text-orange-200 ring-orange-400/40",
  inactive: "bg-slate-500/15 text-slate-200 ring-slate-400/40",
  archived: "bg-slate-500/15 text-slate-200 ring-slate-400/40",
  draft: "bg-slate-500/15 text-slate-200 ring-slate-400/40",
  processing: "bg-purple-500/15 text-purple-300 ring-purple-400/40",
};

const getStatusLabel = (status) => {
  const labels = {
    active: 'Active',
    ready: 'Ready',
    audio_pending: 'Audio Pending',
    outline_pending: 'Outline Pending',
    inactive: 'Inactive',
    archived: 'Archived',
    draft: 'Draft',
    processing: 'Processing',
  };
  return labels[status] || 'Draft';
};

const ManagedAuthorTitlesPage = () => {
  const { authorId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [author, setAuthor] = useState(null);
  const [titles, setTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadFlow, setShowUploadFlow] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTitle, setSelectedTitle] = useState(null);
  const [showActionsMenu, setShowActionsMenu] = useState(null);
  const [bspModalTitle, setBspModalTitle] = useState(null);
  
  // Payment gate state (Phase 3)
  const [showChargeConfirmModal, setShowChargeConfirmModal] = useState(false);
  const [chargeCardInfo, setChargeCardInfo] = useState({ last4: null, brand: null });
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [pendingActivateTitle, setPendingActivateTitle] = useState(null);
  
  const fetchAuthor = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API}/publisher/authors/${authorId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAuthor(data);
      } else {
        toast.error('Author not found');
        navigate('/publisher/authors');
      }
    } catch (error) {
      console.error('Error fetching author:', error);
      toast.error('Failed to load author');
    }
  };
  
  const fetchTitles = async (retryCount = 0) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAYS = [1000, 2000, 4000];
    
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API}/publisher/managed-authors/${authorId}/titles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTitles(data.titles || []);
      } else if (response.status >= 500 && retryCount < MAX_RETRIES) {
        // Backend error - retry with backoff
        console.warn(`[ManagedAuthorTitles] ${response.status} error, retrying (${retryCount + 1}/${MAX_RETRIES})...`);
        await new Promise(r => setTimeout(r, RETRY_DELAYS[retryCount] || 2000));
        return fetchTitles(retryCount + 1);
      } else {
        console.error('Failed to fetch titles:', response.status);
        setTitles([]);
      }
    } catch (error) {
      console.error('Error fetching titles:', error);
      // Network error - retry if we haven't exceeded max retries
      if (retryCount < MAX_RETRIES) {
        console.warn(`[ManagedAuthorTitles] Network error, retrying (${retryCount + 1}/${MAX_RETRIES})...`);
        await new Promise(r => setTimeout(r, RETRY_DELAYS[retryCount] || 2000));
        return fetchTitles(retryCount + 1);
      }
      setTitles([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  useEffect(() => {
    fetchAuthor();
    fetchTitles();
  }, [authorId]);
  
  const handleRefresh = () => {
    setRefreshing(true);
    fetchTitles();
  };
  
  const handleUploadComplete = (data) => {
    console.log('[ManagedAuthorTitles] Upload complete:', data);
    toast.success('Manuscript uploaded successfully!');
    setShowUploadFlow(false);
    fetchTitles();
  };
  
  const handleActivate = async (title) => {
    const jobId = title.simple_ingest_job_id;
    if (!jobId) {
      toast.error('No ingest job found for this title');
      return;
    }
    
    try {
      // Phase 3: Check activation fee first
      const checkRes = await xhrFetchAuth(`${API}/payments/activation-fee-check?job_id=${jobId}`);
      
      if (!checkRes.ok) {
        toast.error(checkRes.json?.detail || 'Failed to check activation fee');
        setShowActionsMenu(null);
        return;
      }
      
      const feeCheck = checkRes.json;
      
      if (!feeCheck.needs_payment) {
        // No payment needed — proceed directly
        await performActivation(title);
        return;
      }
      
      if (feeCheck.payment_method_required) {
        setPendingActivateTitle(title);
        setShowPaymentMethodModal(true);
        setShowActionsMenu(null);
        return;
      }
      
      // Payment needed, card on file — show confirm modal
      setPendingActivateTitle(title);
      setChargeCardInfo({ last4: feeCheck.last4, brand: feeCheck.brand });
      setShowChargeConfirmModal(true);
      setShowActionsMenu(null);
      
    } catch (error) {
      console.error('Error checking activation fee:', error);
      toast.error('Failed to check activation fee');
      setShowActionsMenu(null);
    }
  };
  
  const performActivation = async (title) => {
    try {
      const result = await xhrFetchAuth(`${API}/simple-ingest/jobs/${title.simple_ingest_job_id}/activate-manuscript`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (result.ok) {
        toast.success('Title activated');
        setTitles(prev => prev.map(t => t.id === title.id ? { ...t, status: 'active' } : t));
        
        // Auto-index on activation (backend is idempotent — skips if already indexed)
        try {
          await xhrFetchAuthPost(`${API}/manuscripts/${title.manuscript_id}/index`, {});
        } catch {
          // Non-blocking — indexing is secondary to activation
        }
      } else {
        toast.error(result.json?.detail || 'Failed to activate title');
      }
    } catch (error) {
      console.error('Error activating title:', error);
      toast.error('Failed to activate title');
    }
    setShowActionsMenu(null);
  };
  
  const handleChargeConfirmed = async () => {
    setShowChargeConfirmModal(false);
    if (!pendingActivateTitle) return;
    
    try {
      const chargeRes = await xhrFetchAuthPost(`${API}/payments/charge-activation-fee`, {
        job_id: pendingActivateTitle.simple_ingest_job_id
      });
      
      if (!chargeRes.ok || !chargeRes.json?.success) {
        if (chargeRes.json?.reason === 'payment_failed') {
          toast.error(`Payment failed — ${chargeRes.json?.error || 'Unknown error'}. Title was not activated.`);
        } else if (chargeRes.json?.reason === 'payment_method_required') {
          setShowPaymentMethodModal(true);
        } else {
          toast.error(chargeRes.json?.detail || 'Charge failed');
        }
        return;
      }
      
      // Charge succeeded — proceed to activation
      await performActivation(pendingActivateTitle);
    } catch (err) {
      toast.error('Payment failed: ' + err.message);
    } finally {
      setPendingActivateTitle(null);
    }
  };
  
  const handlePaymentMethodAdded = () => {
    setShowPaymentMethodModal(false);
    if (pendingActivateTitle) {
      handleActivate(pendingActivateTitle);
    }
  };
  
  const handleDeactivate = async (title) => {
    try {
      const result = await xhrFetchAuth(`${API}/simple-ingest/jobs/${title.simple_ingest_job_id}/deactivate-manuscript`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (result.ok) {
        toast.success('Title deactivated');
        setTitles(prev => prev.map(t => t.id === title.id ? { ...t, status: 'inactive' } : t));
      } else {
        toast.error(result.json?.detail || 'Failed to deactivate title');
      }
    } catch (error) {
      console.error('Error deactivating title:', error);
      toast.error('Failed to deactivate title');
    }
    setShowActionsMenu(null);
  };
  
  const handleViewDetails = (titleId) => {
    navigate(`/publisher/titles/${titleId}`);
    setShowActionsMenu(null);
  };
  
  const renderTTSStatus = (title) => {
    if (!title.simple_ingest_job_id) return null;
    
    const completed = title.tts_segments_completed || 0;
    const total = title.tts_segments_total || 0;
    
    if (total === 0) return null;
    
    const progress = Math.round((completed / total) * 100);
    const isComplete = completed >= total;
    
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400">
        {isComplete ? (
          <>
            <CheckCircle className="w-3 h-3 text-emerald-400" />
            <span className="text-emerald-400">Audio complete</span>
          </>
        ) : (
          <>
            <Loader2 className="w-3 h-3 animate-spin text-amber-400" />
            <span className="text-amber-400">Audio: {progress}% ({completed}/{total})</span>
          </>
        )}
      </div>
    );
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-50">
        <PublisherNav />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-t-indigo-500 border-r-transparent border-b-indigo-500 border-l-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <PublisherNav />
      
      <main className="mx-auto max-w-7xl px-4 py-8">
        {/* Back Navigation */}
        <Link
          to={`/publisher/authors/${authorId}/console`}
          className="flex items-center gap-2 text-slate-400 hover:text-slate-200 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to {author?.pen_name || 'Author'}&apos;s Console
        </Link>
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div className="flex items-center gap-4">
            {author?.headshot_image_url ? (
              <img
                src={`${BACKEND_URL}${author.headshot_image_url}`}
                alt={author?.pen_name}
                className="w-16 h-16 rounded-xl object-cover border border-slate-600"
              />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-slate-700 border border-slate-600 flex items-center justify-center">
                <User className="w-8 h-8 text-slate-500" />
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-white">{author?.pen_name}&apos;s Titles</h1>
              <p className="text-slate-400">{titles.length} title{titles.length !== 1 ? 's' : ''} published</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className="gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={() => setShowUploadFlow(true)}
              className="gap-2 bg-indigo-600 hover:bg-indigo-500"
            >
              <Plus className="w-4 h-4" />
              Upload New Title
            </Button>
          </div>
        </div>
        
        {/* Titles Grid */}
        {titles.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No titles yet</h3>
            <p className="text-slate-400 mb-6">
              Upload your first manuscript to get started
            </p>
            <Button
              onClick={() => setShowUploadFlow(true)}
              className="gap-2 bg-indigo-600 hover:bg-indigo-500"
            >
              <Upload className="w-4 h-4" />
              Upload First Title
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {titles.map((title) => (
              <div
                key={title.id}
                className="group relative rounded-xl border border-slate-700 bg-slate-800/50 p-4 hover:border-slate-600 transition-all"
              >
                {/* Cover & Info */}
                <div className="flex gap-4">
                  {/* Cover Image */}
                  <div className="w-20 h-28 rounded-lg overflow-hidden bg-slate-700 flex-shrink-0">
                    {hasCoverImage(title) ? (
                      <img
                        src={getBookCoverUrl(title)}
                        alt={title.title_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpen className="w-8 h-8 text-slate-500" />
                      </div>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-white truncate mb-1">
                      {title.title_name || 'Untitled'}
                    </h3>
                    {title.subtitle && (
                      <p className="text-sm text-slate-400 truncate mb-2">{title.subtitle}</p>
                    )}
                    
                    {/* Status Badge */}
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${statusColors[title.status] || statusColors.draft}`}>
                      {getStatusLabel(title.status)}
                    </span>
                    
                    {/* TTS Progress */}
                    <div className="mt-2">
                      {renderTTSStatus(title)}
                    </div>
                    
                    {/* Stats Row */}
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                      {title.total_reader_credits > 0 && (
                        <span className="flex items-center gap-1">
                          <BarChart3 className="w-3 h-3" />
                          {title.total_reader_credits} credits
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Actions Menu */}
                <div className="absolute top-3 right-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => setShowActionsMenu(showActionsMenu === title.id ? null : title.id)}
                  >
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                  
                  {showActionsMenu === title.id && (
                    <div className="absolute right-0 top-full mt-1 w-48 rounded-lg border border-slate-600 bg-slate-800 shadow-xl z-10">
                      <div className="p-1">
                        <button
                          onClick={() => handleViewDetails(title.id)}
                          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-200 hover:bg-slate-700 rounded-md"
                        >
                          <Eye className="w-4 h-4" />
                          View Details
                        </button>
                        
                        {title.status === 'active' ? (
                          <button
                            onClick={() => handleDeactivate(title)}
                            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-amber-300 hover:bg-slate-700 rounded-md"
                          >
                            <PowerOff className="w-4 h-4" />
                            Deactivate
                          </button>
                        ) : (
                          <button
                            onClick={() => handleActivate(title)}
                            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-emerald-300 hover:bg-slate-700 rounded-md"
                          >
                            <Power className="w-4 h-4" />
                            Activate
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Quick Actions Bar */}
                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-700">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 gap-1 text-slate-300 hover:text-white"
                    onClick={() => handleViewDetails(title.id)}
                  >
                    <Eye className="w-4 h-4" />
                    View
                  </Button>
                  {title.status === 'active' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex-1 gap-1 text-slate-300 hover:text-white"
                      onClick={() => navigate(`/listen/${title.id}`)}
                    >
                      <Play className="w-4 h-4" />
                      Play
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 gap-1 text-purple-300 hover:text-purple-200"
                    onClick={() => setBspModalTitle(title)}
                    data-testid={`bsp-btn-${title.id}`}
                  >
                    <Brain className="w-4 h-4" />
                    BSP
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      
      <UniversalFooter />
      
      {/* Upload Flow Modal */}
      {showUploadFlow && (
        <Dialog open={showUploadFlow} onOpenChange={setShowUploadFlow}>
          <DialogContent className="max-w-4xl bg-slate-900 border-slate-700">
            <StreamlinedUploadFlow
              isOpen={showUploadFlow}
              onClose={() => setShowUploadFlow(false)}
              onComplete={handleUploadComplete}
              apiBaseUrl={`${API}/publisher/managed-authors/${authorId}/simple-ingest`}
              publisherAuthorId={authorId}
              publisherAuthorName={author?.pen_name}
              isPublisherManagedAuthor={true}
            />
          </DialogContent>
        </Dialog>
      )}
      
      {/* Click outside to close actions menu */}
      {showActionsMenu && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowActionsMenu(null)}
        />
      )}

      {/* Phase 3: Payment gate modals */}
      <ActivationChargeConfirmModal
        isOpen={showChargeConfirmModal}
        onConfirm={handleChargeConfirmed}
        onCancel={() => { setShowChargeConfirmModal(false); setPendingActivateTitle(null); }}
        last4={chargeCardInfo.last4}
        brand={chargeCardInfo.brand}
      />
      <PaymentMethodRequiredModal
        isOpen={showPaymentMethodModal}
        onSuccess={handlePaymentMethodAdded}
        onDismiss={() => { setShowPaymentMethodModal(false); setPendingActivateTitle(null); }}
      />

      <BSPDetailModal
        isOpen={!!bspModalTitle}
        onClose={() => setBspModalTitle(null)}
        manuscriptId={bspModalTitle?.id}
        bookTitle={bspModalTitle?.title_name}
        apiBase="/api/creator/manuscripts"
        showHistory={false}
      />
    </div>
  );
};

export default ManagedAuthorTitlesPage;
